import { useState } from 'react';
import toast from 'react-hot-toast';
import DataTable from 'react-data-table-component';

import Modal from '../Modal';
import TableLoader from '../TableLoader';
import ConfirmPrompt from '../ConfirmPrompt';

import AddDocumentTypeForm from '../AddDocumentTypeForm';
import UpdateDocumentTypeForm from '../UpdateDocumentTypeForm';

import useDocumentTypes from '../../hooks/useDocumentTypes';

import documentTypeService from '../../services/documentTypeService';
import { formatDateToSydneyTimeZone } from '../../utils/dateUtils';

const AdminDocumentTypes = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [blockingUsages, setBlockingUsages] = useState([]);

  const [isAddDocumentTypeModalOpen, setIsAddDocumentTypeModalOpen] = useState(false);
  const [isUpdateDocumentTypeModalOpen, setIsUpdateDocumentTypeModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [isBlockedModalOpen, setIsBlockedModalOpen] = useState(false);

  const { data, isLoading, mutate } = useDocumentTypes({
    page,
    perPage,
    sortField,
    sortOrder,
    showArchived
  });

  const documentTypes = data?.documentTypes;
  const documentTypeCount = data?.count;

  const handlePageChange = page => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const handleAddClicked = () => {
    setIsAddDocumentTypeModalOpen(true);
  }

  const handleUpdateClicked = (row) => {
    setSelectedDocumentType(row);
    setIsUpdateDocumentTypeModalOpen(true);
  };

  const handleDeleteClicked = (row) => {
    setSelectedDocumentType(row);
    setIsConfirmDeleteModalOpen(true);
  };

  const handleSort = async (column, sortDirection) => {
    setSortField(column.sortField);
    setSortOrder(sortDirection === 'desc' ? 1 : -1);
  };

  const handleAddDocumentTypeFormSubmitted = async (payload) => {
    try {
      setIsCreating(true);

      await documentTypeService.createDocumentType(payload);
      mutate();
      setIsAddDocumentTypeModalOpen(false);
    } catch (error) {
      toast.error('Failed to create document type');
    } finally {
      setIsCreating(false);
    }
  };

  const closeDocumentUploadModal = () => {
    setIsUpdateDocumentTypeModalOpen(false);
    setSelectedDocumentType(null);
  }

  const handleUpdateDocumentTypeFormSubmitted = async (payload) => {
    try {
      setIsUpdating(true);

      await documentTypeService.updateDocumentType({
        documentTypeId: selectedDocumentType._id,
        updateData: payload
      });
      mutate();
      setIsUpdateDocumentTypeModalOpen(false);
    } catch (error) {
      toast.error('Failed to update document type');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleArchiveDocumentType = async (documentType) => {
    try {
      setIsArchiving(true);

      await documentTypeService.updateDocumentType({
        documentTypeId: documentType._id,
        updateData: { archived: !documentType.archived }
      });
      mutate();
      toast.success(documentType.archived ? 'Document type restored' : 'Document type archived');
    } catch (error) {
      toast.error('Failed to update document type');
    } finally {
      setIsArchiving(false);
    }
  };

  const handleDeleteDocumentType = async (documentType) => {
    try {
      setIsDeleting(true);

      await documentTypeService.deleteDocumentType(documentType._id);
      mutate();
      setIsConfirmDeleteModalOpen(false);

      toast.success('Document type deleted');
    } catch (error) {
      const usages = error?.response?.data?.data;
      if (usages) {
        setBlockingUsages(usages);
        setIsBlockedModalOpen(true);
      } else {
        toast.error(error?.response?.data?.message || 'Failed to delete document type');
      }
      setIsConfirmDeleteModalOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    {
      name: 'Name',
      selector: row => row.name,
    },
    {
      name: 'Description',
      selector: row => row.description,
    },
    {
      name: '',
      right: "true",
      cell: row => (
        <div className='flex items-center gap-2'>
          <button
            onClick={() => handleUpdateClicked(row)}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-ase-blue text-white text-nowrap'
          >
            Update
          </button>

          <button
            onClick={() => handleArchiveDocumentType(row)}
            disabled={isArchiving}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-gray-500 text-white text-nowrap'
          >
            {row.archived ? 'Restore' : 'Archive'}
          </button>

          <button
            onClick={() => handleDeleteClicked(row)}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-red-600 text-white text-nowrap'
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  return (
    <>
      <div className='flex flex-col'>
        <div className='flex justify-end px-4 pt-4'>
          <button
            onClick={() => { setShowArchived(!showArchived); setPage(1); }}
            className='text-xs text-gray-500 underline'
          >
            {showArchived ? 'Show active' : 'Show archived'}
          </button>
        </div>

        <DataTable
          columns={columns}
          data={documentTypes}
          striped
          highlightOnHover
          pointerOnHover
          progressPending={isLoading}
          progressComponent={<TableLoader />}
          sortServer
          onSort={handleSort}
          pagination
          paginationServer
          paginationTotalRows={documentTypeCount}
          paginationRowsPerPageOptions={[5, 10, 20, 50]}
          paginationPerPage={perPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />

        {!showArchived && (
          <div className='px-4 pb-4'>
            <button
              className='bg-ase-blue h-[48px] px-4 font-semibold text-white rounded-md flex items-center justify-center w-fit'
              onClick={handleAddClicked}
            >
              Add
            </button>
          </div>
        )}
      </div>

      <Modal isOpen={isAddDocumentTypeModalOpen}>
        <AddDocumentTypeForm
          onSubmit={handleAddDocumentTypeFormSubmitted}
          onCloseClicked={closeDocumentUploadModal}
          isLoading={isCreating}
        />
      </Modal>

      <Modal isOpen={isUpdateDocumentTypeModalOpen}>
        <UpdateDocumentTypeForm
          documentType={selectedDocumentType}
          isLoading={isUpdating}
          onSubmit={handleUpdateDocumentTypeFormSubmitted}
          onCloseClicked={closeDocumentUploadModal}
        />
      </Modal>

      <Modal isOpen={isConfirmDeleteModalOpen}>
        <ConfirmPrompt
          message={`Are you sure you want to delete the ${selectedDocumentType?.name} document type?`}
          onConfirm={() => handleDeleteDocumentType(selectedDocumentType)}
          onCancel={() => setIsConfirmDeleteModalOpen(null)}
          isLoading={isDeleting}
        />
      </Modal>

      <Modal isOpen={isBlockedModalOpen}>
        <div className='bg-white rounded-lg border border-ase-blue w-full md:w-auto md:max-w-[600px]'>
          <h2 className='text-lg font-semibold bg-ase-blue py-2 px-4 text-white'>
            Cannot Delete Document Type
          </h2>

          <div className='px-4 py-4 flex flex-col gap-4'>
            <p className='text-sm font-semibold'>
              <span>{selectedDocumentType?.name}</span> is referenced by {blockingUsages.length} document(s) belonging to the following labourer(s):
            </p>

            <ul className='text-sm border border-ase-blue rounded-md divide-y max-h-60 overflow-y-auto'>
              {blockingUsages.map((u, i) => (
                <li key={i} className='px-3 py-2 flex justify-between gap-4'>
                  <span>{u.labourerName}</span>
                  <span className='text-gray-500 text-nowrap'>{u.expirationDate ? formatDateToSydneyTimeZone(u.expirationDate, 'dd MMM yyyy') : 'No expiry'}</span>
                </li>
              ))}
            </ul>

            <p className='text-sm text-gray-500'>Archive the type to hide it from new uploads while preserving existing records.</p>

            <div className='flex items-center gap-2'>
              <button
                onClick={() => { setIsBlockedModalOpen(false); handleArchiveDocumentType(selectedDocumentType); }}
                disabled={isArchiving}
                className='bg-ase-blue text-white font-semibold px-2 py-1 text-sm rounded-md'
              >
                Archive instead
              </button>
              <button
                onClick={() => setIsBlockedModalOpen(false)}
                className='border border-ase-blue text-ase-blue font-semibold px-2 py-1 text-sm rounded-md'
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default AdminDocumentTypes;
